import React, { useEffect, useState } from "react";
import { useLanguage } from "../../context/LanguageContext";
import api from "../../lib/api.ts";
// currency utilities
import { useCurrencyUtils } from "../../utils/currencyUtils";
import Pagination from "../common/Pagination";
import { FaCheckCircle } from "react-icons/fa";

const ProfilePayment = () => {
  const { translate, language } = useLanguage();
  const isRTL = language === "ar";
  const { convertPrice, displayPriceWithSymbol } = useCurrencyUtils();
  const [paymentData, setPaymentData] = useState([]);
  const authUser = localStorage.getItem("authUser");
  const user = authUser ? JSON.parse(authUser) : null;
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    itemsPerPage: 10,
  });
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchPaymentData = async () => {
      setIsLoading(true);
      try {
        const response = await api.get(
          `/sellers/commissions?page=${pagination.currentPage}&limit=${pagination.itemsPerPage}`,
        );

        if (response.data && response.data.data) {
          setPaymentData(response.data.data);
          setPagination({
            currentPage: response.data.pagination?.currentPage || 1,
            totalPages: response.data.pagination?.totalPages || 1,
            totalItems: response.data.pagination?.totalItems || 0,
            itemsPerPage: response.data.pagination?.itemsPerPage || 10,
          });
        }
      } catch (error) {
        console.error("Error fetching payment data:", error);
      } finally {
        setIsLoading(false);
      }
    };
    fetchPaymentData();
  }, [pagination.currentPage]);

  if (isLoading) {
    return (
      <div
        className=" rounded-lg shadow-md"
        dir={isRTL ? "rtl" : "ltr"}
        lang={language}
      >
        {/* Header */}
        <div className="bg-[#fcfcfc1f] text-white font-medium rounded-t-[16px]">
          <div className="p-[20px] text-lg rounded-t-[12px] font-semibold text-white bg-primary-soft">
            <h2 className="text-base sm:text-lg font-semibold">
              {translate("sellerPayment.heading")}
            </h2>
          </div>
        </div>
        <div className="px-3 py-6">
          <div className="animate-pulse">
            <div className="h-6 bg-page rounded w-1/3 mb-6"></div>
            <div className="space-y-3">
              {[1, 2, 3, 4, 5].map((i) => (
                <div key={i} className="h-16 bg-card rounded"></div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className="bg-card rounded-lg shadow-md"
      dir={isRTL ? "rtl" : "ltr"}
      lang={language}
    >
      {/* Header */}

      <div className="bg-card font-medium rounded-t-[8px] sm:rounded-t-[16px]">
        <div className="flex flex-col lg:flex-row lg:items-center justify-between px-4 sm:px-5 py-3 sm:py-4 rounded-t-[8px] sm:rounded-t-[12px] bg-primary-soft gap-4 min-h-[120px] lg:min-h-[72px]">
          {/* Left Side Grouping */}
          <div className="flex flex-col justify-center">
            <h2 className="text-white text-base sm:text-lg font-semibold whitespace-nowrap">
              {translate("sellerPayment.heading")}
            </h2>
          </div>

          {/* Right Side Grouping (Kept for height and alignment consistency) */}
          <div className="flex flex-col md:flex-row items-start md:items-center gap-3 ms-auto lg:ms-0 w-full lg:w-auto"></div>
        </div>
      </div>

      <div className="px-3 py-6">
        {/* Mobile Card View */}
        <div className="block lg:hidden space-y-4">
          {paymentData.map((transaction) => (
            <div
              key={transaction.id}
              className="rounded-lg shadow-sm border border-gray-200 p-4"
            >
              <div className="space-y-3">
                {/* Vehicle Info */}
                <div className="flex justify-between items-start">
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-white truncate">
                      {transaction.vehicle?.name || "-"}
                    </div>
                    <div className="text-xs text-gray-500">
                      {transaction.vehicle?.lotNumber || "-"}
                    </div>
                  </div>
                  <button
                    className={`px-3 py-1 bg-green-600 text-white rounded-md text-xs whitespace-nowrap ${
                      isRTL ? "mr-2" : "ml-2"
                    }`}
                  >
                    {translate("sellerPayment.buttons.release")}
                  </button>
                </div>

                {/* Financial Details */}
                <div className="grid grid-cols-2 gap-3 text-xs">
                  <div>
                    <div className="text-gray-500">
                      {translate("sellerPayment.labels.soldPrice")}
                    </div>
                    <div className="font-semibold text-green-600">
                      {displayPriceWithSymbol(
                        convertPrice(transaction.soldPrice),
                      )}
                    </div>
                  </div>
                  <div>
                    <div className="text-gray-500">
                      {translate("sellerPayment.labels.commission")}
                    </div>
                    <div className="font-semibold text-blue-600">
                      {displayPriceWithSymbol(
                        convertPrice(transaction.commission),
                      )}
                    </div>
                  </div>
                  <div>
                    <div className="text-gray-500">
                      {translate("sellerPayment.labels.vat5")}
                    </div>
                    <div className="font-semibold text-orange-600">
                      {displayPriceWithSymbol(
                        convertPrice(transaction.vatAmount),
                      )}
                    </div>
                  </div>
                  <div>
                    <div className="text-gray-500">
                      {translate("sellerPayment.labels.withdrawable")}
                    </div>
                    <div className="font-semibold text-white">
                      {displayPriceWithSymbol(
                        convertPrice(
                          (Number(transaction.soldPrice) || 0) -
                            (Number(transaction.totalCommission) || 0),
                        ),
                      )}
                    </div>
                  </div>
                </div>

                {/* Sale Date */}
                <div className="text-xs text-gray-500">
                  {translate("sellerPayment.labels.saleDate")}:{" "}
                  {new Date(transaction.createdAt).toLocaleDateString(
                    language === "ar" ? "ar-AE" : "en-AE",
                    {
                      year: "numeric",
                      month: "short",
                      day: "numeric",
                      hour: "2-digit",
                      minute: "2-digit",
                    },
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Desktop Table View */}
        <div className="hidden lg:block overflow-hidden rounded-lg shadow-sm">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-page">
              <tr>
                <th
                  className={`px-6 py-3 ${
                    isRTL ? "text-right" : "text-left"
                  } text-xs font-medium text-white uppercase tracking-wider`}
                >
                  {translate("sellerPayment.labels.vehicle")}
                </th>
                <th
                  className={`px-6 py-3 ${
                    isRTL ? "text-right" : "text-left"
                  } text-xs font-medium text-white uppercase tracking-wider`}
                >
                  {translate("sellerPayment.labels.soldPrice")}
                </th>
                <th
                  className={`px-6 py-3 ${
                    isRTL ? "text-right" : "text-left"
                  } text-xs font-medium text-white uppercase tracking-wider`}
                >
                  {translate("sellerPayment.labels.commission")}
                </th>
                <th
                  className={`px-6 py-3 ${
                    isRTL ? "text-right" : "text-left"
                  } text-xs font-medium text-white uppercase tracking-wider`}
                >
                  {translate("sellerPayment.labels.vat5")}
                </th>
                <th
                  className={`px-6 py-3 ${
                    isRTL ? "text-right" : "text-left"
                  } text-xs font-medium text-white uppercase tracking-wider`}
                >
                  {translate("sellerPayment.labels.payable")}
                </th>
                <th
                  className={`px-6 py-3 ${
                    isRTL ? "text-right" : "text-left"
                  } text-xs font-medium text-white uppercase tracking-wider`}
                >
                  {translate("sellerPayment.labels.withdrawableAmount")}
                </th>
                <th
                  className={`px-6 py-3 ${
                    isRTL ? "text-right" : "text-left"
                  } text-xs font-medium text-white uppercase tracking-wider`}
                >
                  {translate("sellerPayment.labels.saleDate")}
                </th>
                <th
                  className={`px-6 py-3 ${
                    isRTL ? "text-right" : "text-left"
                  } text-xs font-medium text-white uppercase tracking-wider`}
                >
                  {translate("sellerPayment.labels.action")}
                </th>
              </tr>
            </thead>
            <tbody className="">
              {paymentData.map((transaction) => (
                <tr key={transaction.id} className="border-b border-gray-200">
                  <td className="px-6 py-4 ">
                    <div>
                      <div className="text-sm font-medium text-white">
                        {transaction.vehicle?.name || "-"}
                      </div>
                      <div className="text-sm text-description">
                        {transaction.vehicle?.lotNumber || "-"}
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 text-sm text-white">
                    {displayPriceWithSymbol(
                      convertPrice(transaction.soldPrice),
                    )}
                  </td>
                  <td className="px-6 py-4 text-sm text-white">
                    {displayPriceWithSymbol(
                      convertPrice(transaction.commission),
                    )}
                  </td>
                  <td className="px-6 py-4 text-sm text-white">
                    {displayPriceWithSymbol(
                      convertPrice(transaction.vatAmount),
                    )}
                  </td>
                  <td className="px-6 py-4 text-sm text-white">
                    {displayPriceWithSymbol(
                      convertPrice(transaction.totalCommission),
                    )}
                  </td>
                  <td className="px-6 py-4 text-sm text-white">
                    {displayPriceWithSymbol(
                      convertPrice(
                        (Number(transaction.soldPrice) || 0) -
                          (Number(transaction.totalCommission) || 0),
                      ),
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-center text-sm text-description">
                    {new Date(transaction.createdAt).toLocaleDateString(
                      language === "ar" ? "ar-AE" : "en-AE",
                      {
                        year: "numeric",
                        month: "short",
                        day: "numeric",
                      },
                    )}
                    <br />
                    {new Date(transaction.createdAt).toLocaleTimeString(
                      language === "ar" ? "ar-AE" : "en-AE",
                      {
                        hour: "2-digit",
                        minute: "2-digit",
                      },
                    )}
                  </td>
                  <td className="px-6 py-4 text-sm text-white">
                   <div className={`relative h-10 w-10 ${isRTL ? "mr-2" : "ml-2"}`}>
  <button
    className={`absolute ${isRTL ? "left-0" : "right-0"} top-0 h-10 w-10 hover:w-32 flex items-center bg-green-600 border border-green-500 text-white rounded-lg transition-all duration-300 ease-in-out shadow-sm overflow-hidden z-10 hover:z-30 group`}
  >
    <div className="flex items-center h-full w-full">
      {/* Icon Container: Centered in the square */}
      <div className="flex-shrink-0 w-10 h-10 flex items-center justify-center">
        <FaCheckCircle size={18} />
      </div>

      {/* Text: Reveals on hover */}
      <span className="pr-4 opacity-0 group-hover:opacity-100 transition-opacity duration-300 text-xs font-bold whitespace-nowrap">
        {translate("sellerPayment.buttons.release")}
      </span>
    </div>
  </button>
</div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Empty State */}
        {paymentData.length === 0 && !isLoading && (
          <div className="text-center py-12">
            <div className="text-gray-500">
              <svg
                className="mx-auto h-12 w-12 text-gray-400"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
                />
              </svg>
              <h3 className="mt-2 text-sm font-medium text-white">
                {translate("sellerPayment.messages.noPaymentsFound")}
              </h3>
              <p className="mt-1 text-sm text-description">
                {translate("sellerPayment.messages.noPaymentsAvailable")}
              </p>
            </div>
          </div>
        )}

        {/* Pagination */}
        {pagination.totalPages > 1 && (
          <div className="px-3 lg:px-6 py-4 border-t border-gray-200">
            <Pagination
              totalPages={pagination.totalPages}
              currentPage={pagination.currentPage}
              setCurrentPage={(page) => {
                // You can implement page change logic here
                setPagination({
                  ...pagination,
                  currentPage: page,
                });
                console.log("Page changed to:", page);
              }}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default ProfilePayment;
